import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { formatCurrency, getDefaultCurrency } from '../../utils/currency';
import { planFeatureLabel } from '../../utils/planFeatures';
import { BrandButton, Highlight, Kicker, Squiggle } from './ui';

/*
 * Figma "Prices" (node 35715:553). Plan prices and features come from
 * Admin → Abonnementen (/api/public/plans); section texts from the landing CMS.
 * Cards 514×815, 29px apart; the middle card is highlighted.
 */
const TIERS = [
  { key: 'starter', monthlyId: 'plan1', yearlyId: 'plan3' },
  { key: 'professional', monthlyId: 'plan2', yearlyId: 'plan4', popular: true },
  { key: 'premium', monthlyId: 'plan5', yearlyId: 'plan6' },
];

const priceFor = (plan, currency) => {
  if (!plan) return 0;
  if (currency === 'USD') return plan.price_usd ?? plan.price;
  if (currency === 'GBP') return plan.price_gbp ?? plan.price;
  return plan.price;
};

const PricingSection = ({ pricing, onAuth, heading = true }) => {
  const { t, i18n } = useTranslation();
  const [plans, setPlans] = useState([]);
  const [billing, setBilling] = useState('monthly');
  const currency = getDefaultCurrency((i18n.language || 'nl').slice(0, 2));

  useEffect(() => {
    axios.get('/public/plans').then((res) => setPlans(res.data.plans || [])).catch(() => setPlans([]));
  }, []);

  const planById = (id) => plans.find((p) => p.id === id);
  const yearlyHint = pricing.yearly_hint || t('pricing.toggle_yearly_hint');

  return (
    <section id="prijzen" className="relative isolate" data-testid="homepage-pricing-section">
      <img
        src="/brand/blob.svg"
        alt=""
        aria-hidden
        className="pointer-events-none absolute left-[-4.6667vw] top-[29.1667vw] -z-10 h-[69.3889vw] w-[106vw] max-w-none select-none"
      />

      <div className="hp-container">
        {heading && <Squiggle className="mt-[clamp(28px,2.7778vw,50px)]" />}

        <div className={`text-center text-brand-navy ${heading ? 'mt-[clamp(32px,3.1667vw,57px)]' : 'mt-[clamp(28px,2.7778vw,50px)]'}`}>
          {heading && (
            <>
              <Kicker>{pricing.kicker}</Kicker>
              <h2 className="hp-h2-bold mx-auto mt-[clamp(18px,1.3333vw,24px)] max-w-[12.5em]">
                <Highlight text={pricing.title} accent="text-brand-ink" />
              </h2>
            </>
          )}
          <p className="mt-[clamp(12px,1.1667vw,21px)] text-[clamp(15px,1vw,18px)] font-medium leading-[clamp(24px,1.9444vw,35px)]">
            {pricing.note}
          </p>

          <div
            className="mx-auto mt-[clamp(20px,1.7778vw,32px)] inline-flex h-[clamp(58px,4.0556vw,73px)] items-center gap-1 rounded-full border-2 border-brand bg-brand-mint p-2"
            data-testid="pricing-billing-toggle"
          >
            {['monthly', 'yearly'].map((mode) => (
              <button
                key={mode}
                type="button"
                onClick={() => setBilling(mode)}
                data-testid={`pricing-toggle-${mode}`}
                aria-pressed={billing === mode}
                className={`flex h-full items-center whitespace-nowrap rounded-full px-[clamp(14px,0.8889vw,16px)] text-[clamp(14px,1vw,18px)] font-medium transition-colors ${
                  billing === mode ? 'bg-brand-navy text-white' : 'text-brand-navy hover:text-brand'
                }`}
              >
                {mode === 'monthly' ? pricing.monthly_label : pricing.yearly_label}
                {mode === 'yearly' && yearlyHint && (
                  <span className={`ml-1.5 whitespace-nowrap text-[0.8em] max-[399px]:hidden ${billing === mode ? 'text-white/70' : 'text-brand-ink'}`}>{yearlyHint}</span>
                )}
              </button>
            ))}
          </div>
        </div>

        <div className="mt-[clamp(40px,4.5556vw,82px)] mx-auto grid max-w-[560px] grid-cols-1 gap-6 lg:max-w-none lg:grid-cols-3 lg:gap-[clamp(14px,1.6111vw,29px)]">
          {TIERS.map((tier) => {
            const plan = planById(billing === 'yearly' ? tier.yearlyId : tier.monthlyId);
            const features = plan ? plan.features.map((f) => planFeatureLabel(f, t)) : [];
            if (plan && billing === 'yearly' && plan.allow_extra_shop && plan.extra_shop_price != null) {
              const amount = priceFor(
                { price: plan.extra_shop_price, price_usd: plan.extra_shop_price_usd, price_gbp: plan.extra_shop_price_gbp },
                currency
              );
              features.push(t('pricing.features.extra_shop_fixed', { amount: formatCurrency(amount, currency) }));
            }

            return (
              <div
                key={tier.key}
                data-testid={`homepage-pricing-card-${tier.key}`}
                className="flex flex-col overflow-hidden rounded-[clamp(20px,1.6667vw,30px)] bg-white shadow-[inset_0_0_0_2px_#EAEAEA] lg:min-h-[min(815px,45.2778vw)]"
              >
                <div className={`h-[clamp(18px,1.5556vw,28px)] flex-shrink-0 ${tier.popular ? 'bg-brand' : 'bg-brand-navy'}`} />
                <div className="flex flex-1 flex-col px-[clamp(20px,1.8333vw,33px)] pb-[clamp(24px,2.4444vw,44px)] pt-[clamp(24px,1.9444vw,35px)] text-center">
                  <h2 className="text-[clamp(16px,1.2222vw,22px)] font-semibold uppercase leading-[12px] tracking-[clamp(4px,0.4444vw,8px)] text-brand-navy">
                    {t(`pricing.plans.${tier.key}.name`)}
                  </h2>
                  <p className="mt-[clamp(14px,1.2222vw,22px)] text-[clamp(14px,1vw,18px)] leading-[12px] text-brand-muted">
                    {t(`pricing.plans.${tier.key}.description`)}
                  </p>
                  <p
                    className="mt-[clamp(18px,1.6111vw,29px)] whitespace-nowrap text-[clamp(44px,3.8889vw,70px)] font-bold leading-[1.2143] text-brand-navy"
                    data-testid={`homepage-pricing-price-${tier.key}`}
                  >
                    {plan ? formatCurrency(priceFor(plan, currency), currency) : '—'}
                  </p>
                  <p className="mt-[clamp(0px,0.3333vw,6px)] text-[clamp(14px,1vw,18px)] font-medium leading-[12px] text-brand-navy">
                    {billing === 'yearly' ? pricing.per_year : pricing.per_month}
                  </p>

                  <ul className="mt-[clamp(28px,4.4444vw,80px)] -mr-[clamp(0px,1.2222vw,22px)] flex-1 space-y-[clamp(10px,1.8333vw,33px)] text-left">
                    {features.map((feature, idx) => (
                      <li
                        key={idx}
                        className="flex items-start gap-[clamp(8px,0.6111vw,11px)] pl-[clamp(0px,0.6667vw,12px)] text-[clamp(15px,1.2222vw,22px)] font-medium leading-[1.2] text-brand-navy"
                      >
                        <img
                          src="/brand/badge-check.svg"
                          alt=""
                          aria-hidden
                          className="h-[clamp(18px,1.3889vw,25px)] w-[clamp(18px,1.3889vw,25px)] flex-shrink-0"
                        />
                        <span className="pt-[0.05em]">{feature}</span>
                      </li>
                    ))}
                  </ul>

                  <BrandButton
                    to="register"
                    onAuth={onAuth}
                    variant={tier.popular ? 'solid' : 'outline'}
                    className="mt-[clamp(28px,2.5vw,45px)] w-full"
                    data-testid={`homepage-pricing-cta-${tier.key}`}
                  >
                    {t(`pricing.plans.${tier.key}.cta`)}
                  </BrandButton>
                </div>
              </div>
            );
          })}
        </div>

        <div className="mt-[clamp(32px,3.2222vw,58px)] flex justify-center">
          <BrandButton to="/pricing" data-testid="homepage-pricing-full-link">
            {pricing.all_plans_label}
          </BrandButton>
        </div>
      </div>
    </section>
  );
};

export default PricingSection;
